import * as React from 'react';
import { PropsWithChildren } from 'react';

import cn from 'classnames';

import { ITheme, theme, TooltipModel } from '@notino/react-styleguide';

import { StyledComponent } from '@linaria/react';

import {
  getArrowPositionClass,
  TOOLTIP_ARROW_BG_COLOR_CCS_VAR,
} from './getArrowPositionClass';
import {
  getPositionClass,
  getTooltipOffsetClass,
  getTooltipPositionClass,
  TOOLTIP_SIDE_OFFSET_CSS_VAR,
} from './getTooltipPositionClass';
import { Portal } from './Portal';
import {
  Content,
  Placeholder,
  RelativeWrapper,
  TooltipWrapper,
  Wrapper,
} from './styled';

type Props = PropsWithChildren<{
  content: React.ReactNode;
  position?: TooltipModel.Position;
  backgroundColor?: BackgroundColor;
  interactive?: boolean;
  isOpen?: boolean;
  dataTestId?: string;
  disabled?: boolean;
  onShow?: () => void;
  onHide?: () => void;
}>;

export type BackgroundColor = keyof ITheme['color']['background'];

type CompoundComponent = React.FC<Props> & {
  Content: StyledComponent<
    React.ClassAttributes<HTMLDivElement> &
      React.HTMLAttributes<HTMLDivElement> & {
        shouldWrapText?: boolean;
      }
  >;
};

export const PortalTooltip: CompoundComponent = ({
  children,
  content,
  onShow,
  onHide,
  disabled,
  interactive = false,
  isOpen: isOpenProp,
  backgroundColor,
  dataTestId,
  position: initialPosition = TooltipModel.Position.topCenter,
}) => {
  const [position, setPosition] = React.useState(initialPosition);
  const [isOpen, setIsOpen] = React.useState(false);
  const [placeholderStyle, setPlaceHolderStyle] =
    React.useState<React.CSSProperties>({});

  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const tooltipWrapperRef = React.useRef<HTMLDivElement>(null);
  const distanceFromArrowToWindowEdge = React.useRef(Number.MAX_VALUE);

  const handleSetStyles = React.useCallback((el: HTMLElement) => {
    const { left, top, width } = el.getBoundingClientRect();
    setPlaceHolderStyle({
      top: top + window.pageYOffset,
      left: left + width / 2,
    });
  }, []);

  const handleShowTooltip = (
    e: React.MouseEvent<HTMLDivElement> | React.FocusEvent<HTMLDivElement>
  ) => {
    if (disabled) {
      return;
    }
    const el = e.currentTarget as HTMLElement;
    handleSetStyles(el);
    setIsOpen(true);
    onShow?.();
  };

  const handleHideTooltip = () => {
    if (disabled) {
      return;
    }
    setIsOpen(false);
    onHide?.();
  };

  React.useEffect(() => {
    if (!wrapperRef.current) {
      return;
    }
    const { left, right, width } = wrapperRef.current.getBoundingClientRect();
    const gutter = 4;
    // since arrow is in the middle of wrapped component
    // I add 'width/2' to each computation
    distanceFromArrowToWindowEdge.current = Math.min(
      left + width / 2 - gutter,
      window.innerWidth - right + width / 2 - gutter
    );
  });

  React.useEffect(() => {
    const scrollEvent = () => {
      setIsOpen((prev) => {
        if (prev) {
          onHide?.();
        }
        return false;
      });
    };

    window.addEventListener('scroll', scrollEvent);

    return () => {
      window.removeEventListener('scroll', scrollEvent);
    };
  }, [onHide]);

  React.useEffect(() => {
    if (wrapperRef.current) {
      handleSetStyles(wrapperRef.current);
    }
  }, [handleSetStyles]);

  const positionRef = React.useRef(position);
  React.useEffect(() => {
    positionRef.current = position;
  });

  // adjust position if tooltip gets off screen
  React.useEffect(() => {
    const el = tooltipWrapperRef.current;
    if (!el) {
      return;
    }
    const { left, width } = el.getBoundingClientRect();

    const overflowsFromRight = left + width > window.innerWidth;
    if (left < 0 && overflowsFromRight) {
      return;
    }

    // using positionRef instead of position to prevent infinite update loop
    if (positionRef.current.startsWith('top')) {
      if (left < 0) {
        setPosition(TooltipModel.Position.topLeft);
      }
      if (overflowsFromRight) {
        setPosition(TooltipModel.Position.topRight);
      }
    } else {
      if (left < 0) {
        setPosition(TooltipModel.Position.bottomLeft);
      }
      if (overflowsFromRight) {
        setPosition(TooltipModel.Position.bottomRight);
      }
    }
  }, [placeholderStyle]);

  const isVisible = typeof isOpenProp !== 'undefined' ? isOpenProp : isOpen;

  return (
    <Wrapper
      ref={wrapperRef}
      onMouseEnter={handleShowTooltip}
      onMouseLeave={handleHideTooltip}
      onClick={handleShowTooltip}
      onFocus={handleShowTooltip}
      onBlur={handleHideTooltip}
    >
      {children}

      <Portal>
        <Placeholder style={placeholderStyle} data-testid={dataTestId}>
          <RelativeWrapper
            visible={isVisible}
            style={
              {
                [TOOLTIP_ARROW_BG_COLOR_CCS_VAR]: backgroundColor
                  ? theme.color.background[backgroundColor]
                  : undefined,
              } as React.CSSProperties
            }
            className={getArrowPositionClass({ position })}
          >
            <TooltipWrapper
              ref={tooltipWrapperRef}
              visible={isVisible}
              backgroundColor={backgroundColor}
              style={
                {
                  [TOOLTIP_SIDE_OFFSET_CSS_VAR]: `${Math.min(
                    distanceFromArrowToWindowEdge.current,
                    30
                  )}px`,
                } as React.CSSProperties
              }
              className={cn(
                getTooltipPositionClass({ position }),
                getPositionClass(position),
                getTooltipOffsetClass({ interactive })
              )}
            >
              {content}
            </TooltipWrapper>
          </RelativeWrapper>
        </Placeholder>
      </Portal>
    </Wrapper>
  );
};

PortalTooltip.Content = Content;
